import React, {useState} from "react";
import {
    Badge,
    Body1,
    Button,
    Combobox,
    Label,
    makeStyles, MessageBar,
    Option,
    Spinner,
    Text,
    Title3,
    tokens
} from "@fluentui/react-components";
import {ArrowLeft24Regular, Checkmark24Regular, Dismiss16Regular} from "@fluentui/react-icons";
import {useTeamMembersMutation} from "../../../services/useTeamMembersMutation.ts";
import {useGraphQuery} from "../../../services/useGraphQuery.ts";
import {Team} from "../../../services/types.ts";
import {teamsGroupStepAtom, teamsGroupUsersAtom, errorAtom, groupsTabViewAtom} from "../../../store.ts";
import {useSetAtom} from "jotai";
import {useTranslation} from "react-i18next";

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        padding: '16px',
    },
    buttonContainer: {
        display: 'flex', 
        gap: '8px', 
        marginTop: '16px',
        '@media (max-width: 640px)': {
            flexDirection: 'column',
            width: '100%'
        }
    },
    primaryButton: {
        marginLeft: 'auto',
        '@media (max-width: 640px)': {
            marginLeft: 0,
            marginTop: '8px'
        }
    },
    formField: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        marginRight: '16px',
        width: '100%',
    },
    selectedTeams: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        marginTop: '16px',
    },
    teamBadge: {
        display: 'flex',
        alignItems: 'center',
        backgroundColor: tokens.colorNeutralBackground1,
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: '16px',
        padding: '4px 8px',
        gap: '4px',
    },
    dismissIcon: {
        cursor: 'pointer',
    },
    comboBox: {
        maxWidth: '400px',
        width: '100%',
        '@media (min-width: 641px) and (max-width: 1024px)': {
            maxWidth: '100%',
        },
    },
    noTeams: {
        color: tokens.colorNeutralForeground3,
        fontStyle: 'italic',
    },
    actionButtons: {
        display: 'flex',
        gap: '8px',
        marginTop: '16px',
        marginLeft: 'auto',
    },
    participantsList: {
        marginTop: '16px',
    },
    participantsHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '12px',
    },
    searchBox: {
        display: 'flex',
        alignItems: 'center',
        maxWidth: '300px',
    },
    participantRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 0',
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    userInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
    },
    userDetails: {
        display: 'flex',
        flexDirection: 'column',
    },
    userEmail: {
        color: tokens.colorNeutralForeground3,
        fontSize: '12px',
        textAlign: 'right',
        width: '100%',
        '@media (max-width: 640px)': {
            textAlign: 'left',
        }
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '12px',
        padding: '24px 0',
    },
    tableContainer: {
        maxHeight: '400px',
        overflowY: 'auto',
    },
    tableHeaderActions: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        margin: '12px 0',
    },
    filtersSection: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
    },
    stepHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '16px',
    },
    headerContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    backButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
    },
    teamDropdown: {
        minWidth: '180px',
    },
    selectedTeamsSummary: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        marginBottom: '16px',
        padding: '12px',
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: '4px',
    },
    checkboxColumn: {
        width: '40px',
        padding: '0 4px',
    },
});

export const SelectTeamsStep: React.FC = () => {
    const styles = useStyles();
    const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
    const setGroupView = useSetAtom(groupsTabViewAtom)
    const setLoadedUsers = useSetAtom(teamsGroupUsersAtom)
    const setCurrentStep = useSetAtom(teamsGroupStepAtom)
    const setError = useSetAtom(errorAtom)
    const {t} = useTranslation();

    const {
        data: teams,
        isLoading: teamsLoading,
        error: teamsError
    } = useGraphQuery<Team[]>('me/joinedTeams')

    const loadTeamMembersMutation = useTeamMembersMutation({
        onSuccess: (data) => {
            setLoadedUsers(data);
            setCurrentStep('editParticipants');
        },
        onError: (error) => {
            setError(error?.message);
        },
        onSettled: () => {
            setSelectedTeamIds([]);
        }
    });

    const handleTeamSelectionChange = (
        _event: React.SyntheticEvent,
        data: { selectedOptions: string[] }
    ) => {
        setSelectedTeamIds(data.selectedOptions);
    };

    const removeSelectedTeam = (teamId: string) => {
        setSelectedTeamIds(selectedTeamIds.filter(id => id !== teamId));
    };

    const handleClearSelection = () => {
        setSelectedTeamIds([]);
    };

    const handleLoadParticipants = async () => {
        const selectedTeams = teams?.filter(team => selectedTeamIds.includes(team.id))
        loadTeamMembersMutation.mutate(selectedTeams!);
    };

    return <>
        <Title3>{t("text.create-audience")}</Title3>
        <MessageBar intent="info">{t("message.audiences-select-teams")}</MessageBar>

        {teamsLoading ? (
            <Spinner label={t("text.loading-teams")}></Spinner>
        ) : teamsError ? (
            <Text style={{color: tokens.colorPaletteRedForeground1}}>{teamsError?.message}</Text>
        ) : (
            <div className={styles.formField}>
                <Label htmlFor="team-selector">{t("text.select-teams")}</Label>
                <Combobox
                    id="team-selector"
                    className={styles.comboBox}
                    multiselect
                    placeholder={t("placeholder.select-teams")}
                    selectedOptions={selectedTeamIds}
                    onOptionSelect={handleTeamSelectionChange}
                >
                    {teams?.map(team => (
                        <Option key={team.id} value={team.id}>
                            {team.displayName}
                        </Option>
                    ))}
                </Combobox>

                <div className={styles.selectedTeams}>
                    {selectedTeamIds.length > 0 ? (
                        (teams || []).filter(team => selectedTeamIds.includes(team.id)).map(team => (
                            <Badge
                                key={team.id}
                                icon={<Dismiss16Regular
                                    style={{cursor: "pointer", zIndex: 1000}}
                                    onClick={() => removeSelectedTeam(team.id)}
                                />}
                                iconPosition="after"
                                appearance="filled">{team.displayName}
                            </Badge>
                        ))
                    ) : (
                        <Body1 className={styles.noTeams}>{t("text.no-teams")}</Body1>
                    )}
                </div>

                <div className={styles.buttonContainer}>
                    <Button
                        appearance="subtle"
                        icon={<ArrowLeft24Regular/>}
                        onClick={() => setGroupView('list')}
                        className={styles.backButton}
                    >
                        {t("button.back-to-audiences-list")}
                    </Button>
                    <Button
                        appearance="subtle"
                        onClick={handleClearSelection}
                        disabled={selectedTeamIds.length === 0}
                    >
                        {t("button.clear")}
                    </Button>
                    {selectedTeamIds.length > 0 && (
                        <Button
                            className={styles.primaryButton}
                            appearance="primary"
                            onClick={handleLoadParticipants}
                            icon={loadTeamMembersMutation.isPending ? <Spinner size="extra-tiny"/> :
                                <Checkmark24Regular/>}
                            disabled={selectedTeamIds.length === 0 || loadTeamMembersMutation.isPending}
                        >
                            {t("button.load-participants")}
                        </Button>
                    )}
                </div>
            </div>
        )}</>
}